import React, {useState, useEffect, useRef} from 'react'
import {FiChevronDown} from 'react-icons/fi'
import './Autosuggest.scss'

interface AutosuggestProps {
  items: Array<{
    id: number | string
    name: string
  }>
  onSelect: (id: number) => void
  selectedItem?: string
  placeholder: string
  inputValue: string
  setInputValue: React.Dispatch<React.SetStateAction<string>>
}

const filterItems = (
  inputValue: string,
  items: Array<{
    id: number | string
    name: string
  }>
) => {
  return items
    .filter(item => item.name.toLowerCase().includes(inputValue.toLowerCase()))
    .sort((a, b) => {
      const aStartsWith = a.name.toLowerCase().startsWith(inputValue.toLowerCase())
      const bStartsWith = b.name.toLowerCase().startsWith(inputValue.toLowerCase())

      if (aStartsWith && !bStartsWith) return -1
      if (!aStartsWith && bStartsWith) return 1
      return a.name.localeCompare(b.name)
    })
}

const Autosuggest: React.FC<AutosuggestProps> = ({items, onSelect, selectedItem, placeholder, inputValue, setInputValue}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const selectedRef = useRef<HTMLDivElement>(null)

  const filteredItems = inputValue ? filterItems(inputValue, items) : items

  useEffect(() => {
    if (selectedItem) {
      setInputValue(selectedItem)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem])

  useEffect(() => {
    if (selectedIndex >= 0) {
      selectedRef.current?.scrollIntoView({
        block: 'nearest'
      })
    }
  }, [selectedIndex])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value)
    setSelectedIndex(-1)
    setIsOpen(true)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      setSelectedIndex(prev => (prev < filteredItems.length - 1 ? prev + 1 : prev))
    } else if (e.key === 'ArrowUp') {
      setSelectedIndex(prev => (prev > 0 ? prev - 1 : prev))
    } else if (e.key === 'Enter') {
      if (selectedIndex >= 0) {
        handleSelect(filteredItems[selectedIndex])
      } else if (filteredItems.length === 1) {
        handleSelect(filteredItems[0])
      }
    }
  }

  const handleSelect = (item: any) => {
    setInputValue(item.name)
    setIsOpen(false)
    onSelect(item.id)
  }

  const handleBlur = () => {
    setIsOpen(false)

    if (!selectedItem) {
      setInputValue('')
    } else if (selectedItem !== inputValue) {
      setInputValue(selectedItem)
    }
  }

  return (
    <div className="autosuggest">
      <div className="autosuggest__input-wrapper">
        <input
          className={`autosuggest__input-wrapper-input ${isOpen ? 'is-open' : ''}`}
          type="text"
          value={inputValue || ''}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsOpen(true)}
          onBlur={handleBlur}
          placeholder={placeholder}
          data-1p-ignore="true"
        />
        <FiChevronDown onClick={() => setIsOpen(!isOpen)} />
      </div>
      {isOpen && (
        <div className="autosuggest__items">
          {filteredItems.map((item, index) => (
            <div
              className={`${index === selectedIndex ? 'is-highlighted' : ''}`}
              key={item.id}
              ref={index === selectedIndex ? selectedRef : null}
              onMouseDown={e => {
                e.preventDefault()
                handleSelect(item)
              }}
              onTouchEnd={e => {
                e.preventDefault()
                handleSelect(item)
              }}
            >
              {item.name}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default Autosuggest
